import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { FlaskConical, Library, Settings as SettingsIcon, type LucideIcon } from 'lucide-react';
import { LibraryPage } from './pages/LibraryPage';
import { IngredientsPage } from './pages/IngredientsPage';
import { SettingsPage } from './pages/SettingsPage';
import { DrinkPage } from './pages/DrinkPage';
import { EditorPage } from './pages/EditorPage';
import { ComparePage } from './pages/ComparePage';
import { ToastProvider, useToast } from './components';
import { api } from './api';
import { SIGNED_OUT_EVENT, UNAUTHORIZED_EVENT } from './events';
import { ErrorMessage, Skeleton } from './components';
import { DescribePage } from './pages/DescribePage';
import { LoginPage } from './pages/LoginPage';
import { applyTheme } from './theme';
import { useShortcuts } from './shortcuts';
import { useCallback, useEffect, useState, type ReactNode } from 'react';

const NAV: { to: string; label: string; icon: LucideIcon }[] = [
  { to: '/', label: 'Library', icon: Library },
  { to: '/ingredients', label: 'Ingredients', icon: FlaskConical },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
];

/** Applies the saved theme once the settings load; the Settings page re-applies on change. */
function AppEffects() {
  const toast = useToast();
  useShortcuts(toast);
  useEffect(() => {
    api.getSettings().then(
      (s) => applyTheme(s.theme),
      () => {}, // offline or server error: keep the OS / remembered theme
    );
  }, []);
  return null;
}

/**
 * In server mode (MUDDLE_AUTH=password) nothing loads until you sign in. Desktop and local dev
 * report "authenticated" straight away, so this renders the app immediately there.
 */
function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'loading' | 'in' | 'out' | 'error'>('loading');
  const check = useCallback(() => {
    setState('loading');
    api.authSession().then(
      (s) => setState(s.authenticated ? 'in' : 'out'),
      () => setState('error'),
    );
  }, []);
  useEffect(() => {
    check();
    const out = () => setState('out');
    window.addEventListener(UNAUTHORIZED_EVENT, out);
    window.addEventListener(SIGNED_OUT_EVENT, out);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, out);
      window.removeEventListener(SIGNED_OUT_EVENT, out);
    };
  }, [check]);

  if (state === 'loading') {
    return (
      <div className="page">
        <Skeleton height={120} />
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="page">
        <ErrorMessage message="Couldn't reach the Muddle server." onRetry={check} />
      </div>
    );
  }
  if (state === 'out') return <LoginPage onSignedIn={() => setState('in')} />;
  return <>{children}</>;
}

export function App() {
  return (
    <ToastProvider>
      <AuthGate>
        <AppEffects />
        <div className="mu-shell">
          <aside className="mu-sidebar">
            <div className="mu-brand">
              <img src="/logo.svg" alt="" width={32} height={32} />
              <span>Muddle</span>
            </div>
            <Nav />
          </aside>
          <main className="mu-main">
            <Routes>
              <Route path="/" element={<LibraryPage />} />
              <Route path="/drinks/new" element={<EditorPage />} />
              <Route path="/drinks/ask" element={<DescribePage />} />
              <Route path="/drinks/:id" element={<DrinkPage />} />
              <Route path="/drinks/:id/edit" element={<EditorPage />} />
              <Route path="/drinks/:id/compare" element={<ComparePage />} />
              <Route path="/ingredients" element={<IngredientsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Nav bottom />
        </div>
      </AuthGate>
    </ToastProvider>
  );
}

/** Sidebar on wide screens, bottom bar on narrow (switched in CSS). */
function Nav({ bottom = false }: { bottom?: boolean }) {
  return (
    <nav className={bottom ? 'mu-tabbar' : 'mu-nav'} aria-label="Main">
      {NAV.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} end={to === '/'} className="mu-nav-item">
          <Icon size={20} aria-hidden />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
